import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface BuildInfo {
  version: string;
  commit: string;
  describe: string;
  dirty: boolean;
  built_at: string | null;
  api_revision: string;
}

/**
 * The copyright and REMAGINE attribution shown at the foot of every page.
 *
 * Extracted from `Layout.tsx` so the **public** pages can carry it too. Layout wraps only the
 * signed-in app, so About, Privacy, Terms and the sign-in screen had no footer at all — the
 * attribution disappeared on exactly the pages a visitor without an account sees.
 *
 * One component rather than four copies: the year range and the REMAGINE link would otherwise
 * drift apart, and this is the sort of text nobody thinks to update in more than one place.
 */

// 2026 is when ShoutVector shipped; the range stays a single year until the next one.
const START_YEAR = 2026;

interface Props {
  /**
   * `contained` sits inside the signed-in Layout, which supplies its own page chrome and
   * background. `standalone` is for the public pages, which have no Layout — it adds the top
   * margin and border that Layout would otherwise provide.
   */
  variant?: 'contained' | 'standalone';
}

export const SiteFooter: React.FC<Props> = ({ variant = 'contained' }) => {
  const { token } = useAuth();
  const currentYear = new Date().getFullYear();
  const yearRange = currentYear === START_YEAR ? `${START_YEAR}` : `${START_YEAR}-${currentYear}`;
  const [build, setBuild] = React.useState<BuildInfo | null>(null);

  /**
   * The running build, fetched rather than compiled in.
   *
   * Only when signed in: the endpoint is authenticated, because a version string tells an
   * anonymous visitor which known vulnerabilities to try — and the public pages share this
   * footer, so compiling it into the bundle would put it on the sign-in screen.
   */
  React.useEffect(() => {
    if (!token) {
      setBuild(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_URL}/api/version`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!cancelled && data) setBuild(data);
      })
      // A missing version is not worth an error anywhere on screen
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box
      component="footer"
      sx={{
        py: 2,
        px: 3,
        textAlign: 'center',
        ...(variant === 'contained'
          ? {
              mt: 'auto',
              // grey[200] stayed a light band in dark mode; the paper colour follows the theme
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[200],
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }
          : {
              // Standalone pages sit on the page background, so the footer stays transparent
              // and is separated by space rather than a band of colour.
              mt: 4,
            }),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        © {yearRange} ShoutVector - All Rights Reserved - Powered by{' '}
        <a
          href="https://www.remagine.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'none', fontWeight: 'bold' }}
        >
          REMAGINE
        </a>
        {/* The version sits on the same line as everything else (Ian's ask, v1.36.3) rather
            than on its own line below. The commit is what actually answers "is my fix
            deployed?", so it is in the tooltip rather than hidden behind an ssh session. A dirty
            build is called out: it was made from uncommitted code and cannot be reproduced from
            the commit it names. */}
        {build && (
          <>
            {' - '}
            <Tooltip
              arrow
              title={
                <span>
                  Commit {build.commit} ({build.describe})
                  {build.dirty && ' — built from uncommitted changes'}
                  <br />
                  {build.built_at ? `Built ${new Date(build.built_at).toLocaleString()}` : 'Build time unknown'}
                  <br />
                  API revision {build.api_revision}
                </span>
              }
            >
              <span style={{ cursor: 'help' }}>
                v{build.version}
                {build.dirty && ' (dirty)'}
              </span>
            </Tooltip>
          </>
        )}
      </Typography>
    </Box>
  );
};
